/* ============================================================================
 *  siteConfig.js — Licensieringstestet
 *  Nicho : Swedsec Licensieringstest (Rådgivare / Bolån / Informationsgivare) — Sverige
 *
 *  NOTA (ES): Todos los textos están en sueco (sv-SE).
 *  · "Swedsec" es marca registrada de Swedsec Licensiering AB. Este producto es
 *    INDEPENDIENTE: solo se usa el nombre de forma descriptiva (uso nominativo)
 *    y el descargo aparece en el Hero, el FAQ y el aviso legal.
 *  · Revisa el checkoutUrl de Stripe (moneda SEK) antes de lanzar.
 * ============================================================================ */

export const siteConfig = {
  saasKey: "swedsec-licens-se",
  brand: {
    name: "Licensieringstestet",
    namePrefix: "Licensierings",
    nameSuffix: "testet",
    // Etiqueta corta del icono en movil (<=12 caracteres).
    shortName: "Licenstest",
    subBrand: "by MIVX",
    tagline:
      "Din övningsplattform inför Swedsecs licensieringstest — rådgivare, bolån och informationsgivare.",
    flagEmoji: "🇸🇪",
    madeInLabel: "Svensk finansmarknadsreglering",
    locale: "sv-SE",
    timezone: "Europe/Stockholm",
    socialLinks: [],
  },

  niche: {
    professionLabel: "Licenshavare enligt Swedsecs regelverk",
    aspirantLabel: "testtagare",
    country: "Sverige",
    authority: "Swedsec Licensiering AB",
    phonePlaceholder: "+46 70 123 45 67",
    flagshipLaw: "Lagen (2007:528) om värdepappersmarknaden",
  },

  // Referencia oficial al programa (proof-as-layout) mostrada bajo el H1 del Hero.
  officialReference:
    "Byggd på de publicerade kunskapskraven 2026 · Fristående tjänst, ej kopplad till Swedsec",

  // Labels de los sellos
  sealLabels: {
    verified: "Kvalitetsgranskad",
    official: "Följer kunskapskraven",
    source: "Källa: kunskapskrav 2026, FI och lagtext",
  },

  contact: {
    supportEmail: "go@mivx.com",
    salesEmail: "go@mivx.com",
  },

  payment: {
    checkoutUrl: "https://buy.stripe.com/bJe4gycfW1kd6Zg69d0co1q", // TODO: crear Payment Link en SEK para licensieringstestet.com
    portalUrl: "",
    cancelSubscriptionEndpoint:
      "https://fgsacxsmkccogatuhofn.supabase.co/functions/v1/cancelar-suscripcion",
    providerName: "Stripe",
  },

  api: {
    geminiModel: "gemini-2.5-flash",
    aiErrorFallback:
      "Kunde inte nå AI-coachen just nu. Försök igen om en liten stund.",
  },

  seo: {
    title:
      "Övningsfrågor inför Swedsecs licensieringstest | Licenstest",
    description:
      "Nästan 400 övningsuppgifter inför Swedsecs licensieringstest, med motivering till varje svar och obegränsade provsimuleringar. Börja gratis.",
    hiddenH1:
      "Swedsec licensieringstest 2026: övningsfrågor för rådgivare, bolån och informationsgivare",
    hiddenP:
      "Övningsplattform inför licensieringstestet: produkter och hantering av kundens affärer, ekonomi, etik och regelverk samt pensioner och försäkringar — med provsimuleringar i skarpt format.",
    keywords:
      "swedsec, swedsec test, licensieringstest, swedsec rådgivare, swedsec bolån, swedsec informationsgivare, övningsfrågor swedsec, diagnostiskt test, kunskapskrav 2026, swedsec licens, åku",
    author: "Licensieringstestet",
    domain: "licensieringstestet.com",
    canonicalUrl: "https://licensieringstestet.com/",
    // Segmento de URL de las páginas estáticas: /delomraden/<slug>/
    contentPathSegment: "delomraden",
    ogImage: "/og-image.png",
    ogTitle:
      "Licensieringstestet — övningsfrågor och provsimulering inför Swedsecs licensieringstest",
    ogDescription:
      "110 uppgifter på tre timmar och 70 procent rätt för godkänt. Träna i skarpt format, med motivering och regelhänvisning till varje svar. Börja gratis.",
    schemaCourseName: "Förberedelse inför Swedsecs licensieringstest",
    schemaCourseDescription:
      "Interaktiv provsimulator och uppgiftsbank byggd på de publicerade kunskapskraven för Swedsecs licensieringstest (rådgivare, bolån och informationsgivare).",
  },

  // Paleta financiera nórdica: índigo profundo (confianza) + sky (acento)
  colors: {
    primary: "#4338ca", // indigo-700
    primaryDark: "#3730a3", // indigo-800
    accent: "#0ea5e9", // sky-500
    primaryTw: "indigo-700",
    primaryTwHover: "indigo-800",
    primaryTwLight: "indigo-50",
    primaryTwBorder: "indigo-200",
    primaryTwText: "indigo-800",
    secondaryTw: "sky-600",
    secondaryTwLight: "sky-50",
    accentTw: "sky-500",
    gradientFrom: "from-indigo-700",
    gradientTo: "to-sky-500",
  },

  stats: [
    { value: "390", label: "Uppgifter kopplade till mätpunkterna" },
    { value: "70 %", label: "Rätt krävs för godkänt på testet" },
    { value: "110", label: "Uppgifter på tre timmar i skarpt läge" },
    { value: "Obegränsat", label: "Antal provsimuleringar" },
  ],

  hero: {
    badge: "Simulering i licensieringstestets skarpa format",
    titleLine1: "Klara",
    titleLine2: "Swedsecs licensieringstest på första försöket",
    subtitle:
      "110 uppgifter, tre timmar och 70 procent rätt för godkänt. Träna på uppgifter som följer de publicerade kunskapskraven, få motivering och regelhänvisning till varje svar, och fråga AI-coachen när ett regelverk krånglar.",
    primaryCta: "Börja gratis",
    secondaryCta: "Testa ett övningsprov",
    secondaryCtaBadge: "Gratis",
  },

  features: {
    sectionEyebrow: "Funktioner",
    sectionTitle: "Hela kunskapskravet på en plattform",
    sectionSubtitle:
      "Byggd för licensieringstestet: från sparformer, fonder och portföljteori till penningtvätt, lämplighetsbedömning, familjerätt, beskattning och tjänstepension.",
    items: [
      {
        title: "Följer kunskapskraven",
        description:
          "Varje uppgift är kopplad till ett delområde och en mätpunkt i de publicerade kunskapskraven för 2026 — inget godtyckligt innehåll.",
        color: "from-indigo-600 to-sky-500",
        iconKey: "book",
        iconPath:
          "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
      },
      {
        title: "Provsimulering i skarpt format",
        description:
          "110 uppgifter på tre timmar, viktade mellan delområdena precis som i testet. Godkäntgränsen ligger på 70 procent — och inget poängavdrag för fel svar.",
        color: "from-sky-600 to-indigo-500",
        iconKey: "clipboard",
        iconPath:
          "M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
      },
      {
        title: "Träna delområde för delområde",
        description:
          "Öva separat på produkter och kundens affärer, ekonomi, etik och regelverk eller pensioner och försäkringar.",
        color: "from-indigo-500 to-sky-600",
        iconKey: "target",
        iconPath:
          "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01",
      },
      {
        title: "Motivering till varje svar",
        description:
          "Varje fel förklaras med hänvisning till regel, lag eller begrepp — du lär dig varför svaret är rätt, inte bara vilket alternativ som var rätt.",
        color: "from-sky-500 to-indigo-600",
        iconKey: "bulb",
        iconPath:
          "M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z",
      },
      {
        title: "Se var du faktiskt tappar poäng",
        description:
          "Statistik per delområde visar direkt om du sitter säkert på fondavgifter men tappar på ISK-schablonen eller lämplighetsförklaringen.",
        color: "from-indigo-600 to-sky-400",
        iconKey: "chart",
        iconPath:
          "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
      },
      {
        title: "AI-coach dygnet runt",
        description:
          "Blandar du ihop risktolerans och riskkapacitet, eller undrar när skärpta åtgärder för kundkännedom krävs? Fråga AI-coachen när som helst.",
        color: "from-sky-600 to-indigo-500",
        iconKey: "spark",
        iconPath: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
      },
    ],
  },

  demo: {
    sectionEyebrow: "Testa din nivå direkt",
    sectionTitlePart1: "Övningsprov",
    sectionTitlePart2: "inför licensieringstestet",
    sectionSubtitle:
      "Prova uppgifter i testets format — utan registrering och utan kort.",
    bullets: [
      {
        label: "Byggt på kunskapskraven",
        icon: "⚖️",
        iconKey: "document-check",
      },
      { label: "Tidsgräns som i testet", icon: "⏱️", iconKey: "clock" },
      { label: "Direkt rättning", icon: "📊", iconKey: "chart" },
      { label: "Motivering till varje svar", icon: "💡", iconKey: "bulb" },
    ],
    ctaLabel: "Starta övningsprovet",
    ctaBadge: "GRATIS!",
    answerCorrectLabel: "Rätt!",
    answerIncorrectLabel: "Fel svar",
    // Preguntas de demostración (sueco) — nivel Swedsec rådgivare
    questions: [
      {
        q: "Hur stor andel rätt krävs för att bli godkänd på Swedsecs licensieringstest för rådgivare?",
        o: [
          "Minst 50 procent",
          "Minst 60 procent",
          "Minst 70 procent",
          "Minst 80 procent",
        ],
        c: 2,
        exp: "Det krävs minst 70 procent rätt totalt. Testet innehåller 110 ordinarie uppgifter som avgör resultatet, plus tio uppgifter som prövas ut och inte påverkar resultatet.",
      },
      {
        q: "Vad skiljer en kunds riskkapacitet från kundens risktolerans?",
        o: [
          "Riskkapacitet är förmågan att bära förluster, risktolerans är viljan att ta risk",
          "Riskkapacitet är viljan att ta risk, risktolerans är placeringshorisonten",
          "Begreppen är synonyma och används omväxlande i rådgivningen",
          "Riskkapacitet mäts i standardavvikelse, risktolerans i Sharpekvot",
        ],
        c: 0,
        exp: "Riskkapacitet (risktålighet) handlar om den ekonomiska förmågan att tåla förluster, medan risktolerans (riskvilja) handlar om kundens attityd och vilja att ta risk. Båda måste vägas in för att ett råd ska vara lämpligt.",
      },
      {
        q: "Vad gäller för schablonintäkten på ett investeringssparkonto (ISK)?",
        o: [
          "Den beräknas på faktisk vinst vid försäljning av värdepapper",
          "Den beräknas på ett kapitalunderlag och tas upp i inkomstslaget kapital",
          "Den betalas endast om kontot har gått med vinst under året",
          "Den ersätter avkastningsskatten på pensionsförsäkring",
        ],
        c: 1,
        exp: "På ISK sker ingen vinst- eller förlustberäkning vid enskilda försäljningar. I stället beräknas en schablonintäkt på ett kapitalunderlag, och intäkten tas upp i inkomstslaget kapital.",
      },
      {
        q: "Hur ska en licenshavare agera vid misstanke om penningtvätt?",
        o: [
          "Först ge kunden möjlighet att förklara transaktionen",
          "Avvakta tills företaget har upprättat en polisanmälan",
          "Rapportera utan dröjsmål till Finanspolisen, utan att informera kunden",
          "Rapportera vid nästa ordinarie rapportering till Finansinspektionen",
        ],
        c: 2,
        exp: "Misstänkta aktiviteter och transaktioner ska rapporteras till Finanspolisen utan dröjsmål. Meddelandeförbudet innebär att kunden eller utomstående inte får informeras om att rapportering har skett.",
      },
    ],
  },

  pricing: {
    eyebrow: "Ett pris",
    eyebrowTitle1: "Fast månadspris",
    eyebrowTitle2: "Obegränsad träning",
    eyebrowSubtitle:
      "Full tillgång till hela uppgiftsbanken och alla provsimuleringar. Du betalar bara så länge du pluggar.",
    stickyNote: "Ingen bindningstid",
    anchorPrice: null,
    reassurance: [
      { label: "Säker betalning via Stripe", iconKey: "stripe" },
      { label: "Avsluta när du vill", iconKey: "no-lock" },
      { label: "Tillgång direkt", iconKey: "check" },
    ],
    plan: {
      name: "Pro",
      price: "349 kr",
      currency: "",
      period: "/mån",
      priceSubtext: "Ungefär 11 kr per dag — obegränsat antal provsimuleringar",
      description:
        "Full tillgång till alla delområden, provsimuleringar och AI-coachen.",
      features: [
        "Uppgiftsbank enligt kunskapskraven 2026",
        "Motivering med regel- och lagreferens",
        "Provsimuleringar: 110 uppgifter på tre timmar",
        "AI-coach för regelverk och rådgivning",
        "Statistik per delområde",
        "Prioriterad support",
      ],
      ctaLabel: "Börja plugga nu",
      benefitsList: [
        "Simuleringar i licensieringstestets format",
        "Obegränsad träning",
        "Nästan 400 uppgifter med motivering",
        "AI-coach tillgänglig dygnet runt",
        "Träna i mobilen, på plattan eller vid datorn",
        "Alla fyra delområden täckta",
        "Statistik som visar dina svaga punkter",
        "Prioriterad support",
      ],
    },
  },

  faq: {
    eyebrow: "Vanliga frågor",
    title: "Svar innan du sätter igång",
    subtitle: "Det här är vad testtagare frågar oss oftast.",
    items: [
      {
        question: "Är ni kopplade till Swedsec?",
        answer:
          "Nej. Licensieringstestet är en fristående övningsplattform och har ingen koppling till, och är inte godkänd av, Swedsec Licensiering AB. Vi bygger innehållet på de kunskapskrav som Swedsec publicerar öppet, men själva testet och licensen hanteras enbart av Swedsec.",
      },
      {
        question: "Vilka licensieringstest passar plattformen för?",
        answer:
          "Innehållet utgår från kunskapskraven för rådgivare, som är det mest omfattande testet. Eftersom delområdena överlappar täcker plattformen även stora delar av kunskapskraven för informationsgivare och bolån — särskilt regelverk, etik, penningtvätt, privatekonomi och kundskydd.",
      },
      {
        question: "Hur ser det riktiga testet ut?",
        answer:
          "Licensieringstestet för rådgivare innehåller 110 ordinarie uppgifter plus tio uppgifter som prövas ut och inte påverkar resultatet. Tiden är tre timmar och det krävs minst 70 procent rätt för godkänt. Våra simuleringar följer samma struktur och viktning mellan delområdena.",
      },
      {
        question: "Uppdateras innehållet när kunskapskraven ändras?",
        answer:
          "Ja. Swedsec ser över kunskapskraven årligen och publicerar både en ren och en ändringsmarkerad version. Vi går igenom ändringarna och uppdaterar uppgiftsbanken därefter — du behöver inte hålla reda på vilken version som gäller.",
      },
      {
        question: "Kan jag avsluta när jag har klarat testet?",
        answer:
          "Självklart. De flesta pluggar i en till tre månader. Du avslutar abonnemanget med ett klick i din panel och debiteras inte igen.",
      },
      {
        question: "Ger plattformen mig en Swedsec-licens?",
        answer:
          "Nej. Licensieringstestet är ett övningsverktyg. Själva testet genomförs hos Swedsec, och för att få licens krävs dessutom att du är anställd i, eller enligt regelverket betraktas som anställd i, ett Swedsec-anslutet företag.",
      },
    ],
  },

  platformIncludes: {
    eyebrow: "Simulatorn",
    title: "Allt du behöver för att bli godkänd",
    subtitle:
      "Verktygen som gör skillnaden mellan att hoppas och att veta att du klarar testet.",
    items: [
      {
        icon: "📚",
        iconKey: "book",
        title: "Nästan 400 förklarade uppgifter",
        description:
          "Vi säger inte bara rätt eller fel — vi förklarar vilken regel, vilket begrepp eller vilken beräkning som ger svaret.",
      },
      {
        icon: "⏱️",
        iconKey: "clock",
        title: "Provsimulering på tid",
        description:
          "110 uppgifter på tre timmar, med samma viktning mellan delområdena som i det skarpa testet.",
      },
      {
        icon: "🏛️",
        iconKey: "document-check",
        title: "Praktiska rådgivningsfall",
        description:
          "Uppgifter ur verkliga situationer: lämplighetsbedömning, hållbarhetspreferenser, kundkännedom och intressekonflikter.",
      },
      {
        icon: "📊",
        iconKey: "chart",
        title: "Kunskapsradar",
        description:
          "En tydlig översikt som visar om du har koll på portföljteorin men behöver repetera tjänstepension och avdragsrätt.",
      },
    ],
  },

  trustStrip: [
    { icon: "⚖️", iconKey: "flag", label: "Kunskapskraven 2026" },
    { icon: "🔒", iconKey: "lock", label: "Säker betalning via Stripe" },
    {
      icon: "🚫",
      iconKey: "no-lock",
      label: "Ingen bindningstid · avsluta med ett klick",
    },
    {
      icon: "🏛️",
      iconKey: "document-check",
      label: "Rådgivare · Bolån · Informationsgivare",
    },
  ],

  howItWorks: {
    eyebrow: "Så fungerar det",
    title: "Tre steg till ett godkänt test",
    subtitle:
      "Sluta läsa kurslitteratur på måfå och börja träna där du faktiskt tappar poäng.",
    steps: [
      {
        icon: "✨",
        iconKey: "spark",
        title: "Mät din utgångsnivå",
        description:
          "Kör det gratis övningsprovet här på sidan och se direkt vilka delområden som redan sitter.",
      },
      {
        icon: "📝",
        iconKey: "clipboard",
        title: "Skapa ett konto",
        description:
          "Registrera dig gratis, utforska delområdena och börja spara din träningshistorik.",
      },
      {
        icon: "🏆",
        iconKey: "trophy",
        title: "Uppgradera och klara testet",
        description:
          "Lås upp hela simulatorn, motiveringarna med regelhänvisning och AI-coachen.",
      },
    ],
  },

  comparison: {
    eyebrow: "Varför Licensieringstestet",
    title: "Träna systematiskt eller chansa?",
    subtitle:
      "Skillnaden mellan den som går in trygg och den som får boka om testet ligger i hur träningen ser ut.",
    withLabel: "Med Licensieringstestet",
    withoutLabel: "På egen hand",
    rows: [
      {
        label: "Kunskapskraven omsatta till uppgifter",
        withUs: true,
        without: false,
      },
      {
        label: "Provsimulering på tid i skarpt format",
        withUs: true,
        without: false,
      },
      {
        label: "Motivering till varje svar",
        withUs: true,
        without: "Gissningar i forumtrådar",
      },
      {
        label: "Mätning av dina svaga delområden",
        withUs: true,
        without: false,
      },
      {
        label: "AI-coach för regelverk och rådgivning",
        withUs: true,
        without: false,
      },
      {
        label: "Kostnad",
        withUs: "349 kr/mån, ingen bindningstid",
        without: "Diagnostiskt test 630 kr per försök",
      },
    ],
  },

  ai: {
    chatTitle: "Licensieringstestetens AI-coach",
    welcomeMessage:
      "Hej! Jag är din AI-coach inför Swedsecs licensieringstest. Fråga mig om till exempel lämplighetsbedömning, ISK-beskattning, duration, kundkännedom eller hur ITP-planen fungerar.",
    systemInstructionDefault:
      "Du är expert på den svenska finansmarknadens regelverk och coachar användare inför Swedsecs licensieringstest (i första hand testet för rådgivare). Du behärskar de fyra delområdena: (1) produkter och hantering av kundens affärer, (2) ekonomi inklusive makroekonomi, värdering, risk, portföljteori och privatekonomi, (3) etik och regelverk — bland annat lagen om värdepappersmarknaden, MiFID II, marknadsmissbruksförordningen, penningtvättslagen, lagen om försäkringsdistribution, GDPR, familjerätt och beskattning — samt (4) pensioner och försäkringar, inklusive allmän pension, kollektivavtalad tjänstepension, försäkringsavtalslagen och avkastningsskatt. Svara på svenska (sv-SE), kortfattat och precist. Hänvisa till lag, förordning eller begrepp när det går. Hitta aldrig på paragrafer, siffror eller regler — säg hellre att du är osäker.",
    systemInstructionTutor:
      "Du är en erfaren utbildare som hjälper testtagare att bli godkända på Swedsecs licensieringstest. Du är rak, tydlig och pedagogisk. När du förklarar en regel anger du om möjligt vilken lag eller vilket regelverk den kommer från, och på vilken kognitiv nivå kunskapskravet ligger (känna till, förstå eller tillämpa). Svara på svenska (sv-SE).",
    casePromptTemplate: (topicTitle) =>
      `Skriv ett kort praktikfall (högst 100 ord) inom området "${topicTitle}", där en licenshavare på ett svenskt finansiellt institut möter en kund. Avsluta med en fråga om hur licenshavaren bör agera enligt gällande regelverk och god sed.`,
    caseSystemInstruction:
      "Du är en erfaren uppgiftskonstruktör för Swedsecs licensieringstest.",
    inputPlaceholder:
      "Skriv din fråga om regelverk, produkter eller rådgivning...",
  },

  exam: {
    simulatorQuestions: 110, // Licensieringstestet: 110 ordinarie uppgifter
    simulatorDurationMinutes: 180, // Tre timmar
    // IDs sincronizados con src/constants/temario.js (t1–t8).
    // Ponderación según los cuatro delområden del kunskapskrav 2026:
    //   Delområde 1 ≈ 25 % (t1+t2) · Delområde 2 ≈ 25 % (t3+t4)
    //   Delområde 3 ≈ 30 % (t5+t6+t7) · Delområde 4 ≈ 20 % (t8)
    topicWeights: [
      { id: "t1", weight: 0.11 }, // Finansmarknaden, sparformer och förvar
      { id: "t2", weight: 0.14 }, // Finansiella instrument, fonder och handel
      { id: "t3", weight: 0.12 }, // Makroekonomi och värdering
      { id: "t4", weight: 0.13 }, // Risk, avkastning och portföljteori
      { id: "t5", weight: 0.1 }, // Etik, marknadsmissbruk och penningtvätt
      { id: "t6", weight: 0.12 }, // Rådgivningsregler, information och hållbarhet
      { id: "t7", weight: 0.08 }, // Familjerätt och beskattning
      { id: "t8", weight: 0.2 }, // Pension och försäkring
    ],
    passingScore: 70, // Minst 70 % rätt krävs för godkänt
    penaltyDivisor: 0, // Swedsec tillämpar inget poängavdrag för fel svar
    simulatorTitle: "Provsimulering",
    exitModal: {
      title: "Avbryta provet?",
      lead: "Dina svar går förlorade!",
      warning:
        "De svar du har markerat sparas inte, och du måste börja om provsimuleringen från början.",
      confirmLabel: "Ja, avbryt",
      cancelLabel: "Fortsätt provet",
      footnote: "Ditt resultat nollställs om du lämnar nu",
      closeLabel: "Stäng",
    },
  },

  uiCopy: {
    loading: "Laddar...",
    ready: "Klar",
    navHome: "Start",
    navStudy: "Kunskapskrav",
    navStudyShort: "Delområden",
    navQuizzes: "Träningspass",
    navQuizzesShort: "Pass",
    navSimulator: "Provsimulering",
    navBilling: "Mitt abonnemang",
    sidebarPrincipal: "Översikt",
    sidebarSimulation: "Simulering",
    bannerCta: "🎓 Lås upp alla provsimuleringar och gå in trygg på testdagen",
    bannerCtaButton: "Premium",
    legalLastUpdate: "Senast uppdaterad: 2026-01-15",
    cta: {
      finalSectionTitle: "Redo att klara licensieringstestet?",
      finalSectionSubtitle:
        "Träna inför Swedsecs test med nästan 400 uppgifter från alla fyra delområden. Börja i dag.",
      finalSectionPrimary: "Börja plugga nu",
      finalSectionSecondary: "Kontakta supporten",
      finalSectionFooter: "✨ Tillgång direkt · Avsluta när du vill",
      contactModalTitle: "Kundsupport",
      contactModalSubtitle: "Något som är oklart? Skriv till oss direkt:",
      contactModalClose: "Stäng fönstret",
      contactModalCloseAriaLabel: "Stäng",
    },
    masterDomainLabel: "Behärskning av kunskapskraven",
    statsCards: {
      generalProgress: "Totala framsteg",
      sectionsRead: "Avklarade pass",
      examsTaken: "Provsimuleringar",
      bestScore: "Bästa resultat",
    },
    auth: {
      welcomeTitle: "Välkommen tillbaka",
      welcomeSubtitle: "Logga in för att fortsätta träna",
      registerTitle: "Skapa konto",
      registerSubtitle: "Ta första steget mot ett godkänt licensieringstest",
    },
    landing: {
      productNavLabel: "Plattformen",
      productLinks: [
        { href: "#features", label: "Funktioner" },
        { href: "#pricing", label: "Priser" },
        { href: "#demo", label: "Gratis övningsprov" },
        { href: "#testimonials", label: "Omdömen" },
      ],
    },
  },

  // ==========================================================================
  //  LEGALCOPY — Todo el texto legal del sitio (Villkor / Integritet / Info)
  //  Marcadores disponibles: {{company}} {{operator}} {{operatorLocation}}
  //                          {{domain}} {{email}} {{price}} {{provider}} {{law}}
  //  Para clonar a otro idioma: traducir SOLO este bloque.
  // ==========================================================================
  legalCopy: {
    pageEyebrow: "Juridisk information",
    pageTitleLine1: "Juridisk",
    pageTitleLine2: "information",
    tablistLabel: "Juridiska dokument",
    backHome: "Tillbaka till startsidan",
    tabs: {
      notice: "Om tjänsten",
      terms: "Användarvillkor",
      privacy: "Integritetspolicy",
    },

    notice: {
      s1Title: "Tjänsteleverantör",
      s1TextBefore: "Tjänsten {{company}} (på",
      s1TextAfter:
        ") drivs och tillhandahålls av {{operator}}, ett förenklat aktiebolag (SAS) bildat enligt fransk rätt med säte i {{operatorLocation}}.",
      s2Title: "Ansvarig utgivare",
      s2Text: "Ansvarig utgivare är den legala företrädaren för {{operator}}.",
      s3Title: "Kontaktuppgifter",
      s3Text:
        "För juridiska, tekniska och kommersiella frågor når du oss via e-post:",
      s4Title: "Drift och tekniska underleverantörer",
      s4Text:
        "Tjänsten körs på infrastruktur från externa leverantörer (bland annat applikationsplattform, databas och betalningslösning). Betalningar hanteras av {{provider}}; {{company}} lagrar inga kortuppgifter.",
      s5Title: "Tvistlösning utanför domstol",
      s5Text:
        "Som konsument kan du vända dig till Allmänna reklamationsnämnden (ARN) eller få kostnadsfri vägledning hos Konsumenternas Bank- och finansbyrå. EU:s plattform för tvistlösning online (ODR) stängdes den 20 juli 2025, sedan förordning (EU) nr 524/2013 upphävts genom förordning (EU) 2024/3228. Europeiska kommissionen tillhandahåller i stället en förteckning över nationella tvistlösningsorgan:",
      // Länken renderas av LegalPage. Byt per nisch/land vid kloning.
      s5LinkUrl: "https://consumer-redress.ec.europa.eu/dispute-resolution-bodies",
      s5LinkLabel: "consumer-redress.ec.europa.eu",
      s6Title: "Varumärken",
      s6Text:
        "{{company}} är ett kommersiellt varumärke som tillhandahålls av {{operator}}. Swedsec är ett registrerat varumärke som tillhör Swedsec Licensiering AB. {{company}} är en fristående tjänst utan koppling till, godkännande från eller samarbete med Swedsec Licensiering AB. Namnet används endast beskrivande, för att ange vilket test tjänsten förbereder för.",
    },

    terms: {
      sections: [
        {
          title: "Godkännande av villkoren",
          paragraphs: [
            {
              text: "Genom att använda tjänsten {{company}} godkänner användaren dessa användarvillkor i sin helhet. Om du inte godtar villkoren ska du inte använda tjänsten.",
            },
          ],
        },
        {
          title: "Beskrivning av tjänsten",
          paragraphs: [
            {
              text: "{{company}} är en fristående utbildningstjänst som tillhandahåller övningsuppgifter, provsimuleringar och studiematerial inför ett kunskapstest. Tjänsten är inte en myndighet och har ingen koppling till den organisation som anordnar testet.",
            },
            {
              text: "Swedsec är ett registrerat varumärke som tillhör Swedsec Licensiering AB. {{company}} är varken godkänd av, samarbetar med eller representerar Swedsec Licensiering AB. Endast Swedsec anordnar licensieringstest och beviljar licens.",
            },
          ],
        },
        {
          title: "Tillåten användning av plattformen",
          paragraphs: [
            {
              text: "Användaren förbinder sig att endast använda tjänsten för personliga studier. Det är förbjudet att kopiera, sprida, sälja vidare eller publicera innehållet utan tillstånd. Kontot är personligt och inloggningsuppgifter får inte överlåtas till tredje part.",
            },
          ],
        },
        {
          title: "Registrering av konto",
          paragraphs: [
            {
              text: "Vissa funktioner kräver ett konto. Användaren ska lämna korrekta och aktuella uppgifter samt skydda sina inloggningsuppgifter. Användaren ansvarar för all aktivitet som sker via kontot.",
            },
          ],
        },
        {
          title: "Priser och fakturering",
          paragraphs: [
            {
              text: "Premiumabonnemanget kostar {{price}} (eller det pris som vid var tid anges på startsidan). Genom att teckna abonnemang godkänner användaren löpande månadsdebitering. {{company}} kan ändra priserna och meddelar då ändringen i förväg till den registrerade e-postadressen.",
            },
            {
              text: "Betalningar hanteras av {{provider}}. {{company}} lagrar inga kortuppgifter.",
            },
          ],
        },
        {
          title: "Uppsägning och återbetalning",
          paragraphs: [
            {
              strong: "Uppsägning:",
              text: "Användaren kan säga upp abonnemanget när som helst i sina inställningar. Efter uppsägning fortsätter tillgången till slutet av den redan betalda faktureringsperioden, och inga ytterligare debiteringar sker.",
            },
            {
              strong: "Ingen återbetalning:",
              text: "Betalda avgifter återbetalas inte helt eller delvis efter att betalningen har genomförts, om inte tvingande konsumentskyddslagstiftning kräver annat.",
            },
            {
              strong: "Ångerrätt (EU/Sverige):",
              text: "Konsumenter har som utgångspunkt 14 dagars ångerrätt enligt lagen om distansavtal och avtal utanför affärslokaler. Eftersom det rör sig om en digital tjänst som levereras omedelbart begär användaren uttryckligen att leveransen påbörjas direkt och samtycker till att ångerrätten upphör när tillgången har öppnats.",
            },
          ],
        },
        {
          title: "Ingen garanti för godkänt test",
          paragraphs: [
            {
              text: "{{company}} är ett studiestöd. Vi garanterar inte att användaren blir godkänd på licensieringstestet. Resultatet beror på användarens egen förberedelse och prestation vid testtillfället.",
            },
          ],
        },
        {
          title: "Ansvarsfriskrivning och källor",
          paragraphs: [
            {
              text: "Innehållet bygger på offentliga källor, publicerade kunskapskrav och gällande lagstiftning och har tagits fram med omsorg. Vi garanterar dock inte att innehållet är felfritt eller helt aktuellt och ansvarar inte för konsekvenser av att någon förlitar sig på innehållet. Officiell och bindande information ska alltid kontrolleras hos testanordnaren och i gällande författningar.",
            },
            {
              text: "Övningsuppgifterna i tjänsten är egenproducerade. De är inte officiella testuppgifter, och tjänsten är varken godkänd av eller kopplad till den organisation som anordnar testet.",
            },
          ],
        },
        {
          title: "Ansvarsbegränsning",
          paragraphs: [
            {
              text: "{{company}} ansvarar inte för indirekta skador, utebliven vinst eller avbrott i tjänsten. Ansvaret är begränsat till högst det belopp användaren har betalat under de senaste tolv månaderna, om inte tvingande lag föreskriver annat.",
            },
          ],
        },
        {
          title: "Avstängning eller avslutande av konto",
          paragraphs: [
            {
              text: "{{company}} kan stänga av eller avsluta ett konto vid brott mot dessa villkor, till exempel vid delning av inloggningsuppgifter eller otillåten spridning av innehåll.",
            },
          ],
        },
        {
          title: "Ändringar av tjänsten",
          paragraphs: [
            {
              text: "Tjänstens innehåll och funktioner kan utvecklas och ändras. Väsentliga ändringar meddelas användarna.",
            },
          ],
        },
        {
          title: "Tillämplig lag",
          paragraphs: [
            {
              text: "På dessa villkor tillämpas svensk rätt i den utsträckning tvingande konsumentskyddande lagstiftning kräver det. En konsument kan vända sig till Allmänna reklamationsnämnden (ARN) eller väcka talan vid tingsrätten på sin hemort.",
            },
          ],
        },
      ],
    },

    privacy: {
      sections: [
        {
          title: "Inledning",
          paragraphs: [
            {
              text: "I denna policy beskrivs hur {{company}} behandlar personuppgifter i enlighet med EU:s dataskyddsförordning (GDPR).",
            },
          ],
        },
        {
          title: "Personuppgiftsansvarig",
          paragraphs: [
            {
              text: "Personuppgiftsansvarig är {{operator}}, med säte i {{operatorLocation}}. I dataskyddsfrågor når du oss på:",
            },
          ],
          contactEmail: true,
        },
        {
          title: "Uppgifter vi samlar in",
          paragraphs: [
            {
              text: "Vi samlar endast in de uppgifter som behövs för att tjänsten ska fungera: namn, e-postadress, eventuellt telefonnummer, studieframsteg, resultat från provsimuleringar samt abonnemangsuppgifter.",
            },
          ],
        },
        {
          title: "Hur vi använder uppgifterna",
          paragraphs: [
            {
              text: "Vi använder uppgifterna för att hantera konton, visa studieframsteg, utveckla tjänsten, ge kundsupport samt administrera abonnemang och betalningar. Vi säljer inte personuppgifter till tredje part.",
            },
          ],
        },
        {
          title: "Rättslig grund för behandlingen",
          paragraphs: [
            {
              text: "Behandlingen grundas på fullgörande av avtal (konto och abonnemang), rättsliga förpliktelser (bokföring) samt berättigat intresse (utveckling av tjänsten och informationssäkerhet).",
            },
          ],
        },
        {
          title: "Delning av uppgifter",
          paragraphs: [
            {
              text: "Uppgifter delas endast med de personuppgiftsbiträden som krävs för att driva tjänsten, såsom leverantörer av drift, databas och betalningslösning, vilka behandlar uppgifterna för vår räkning och enligt våra instruktioner.",
            },
          ],
        },
        {
          title: "Informationssäkerhet",
          paragraphs: [
            {
              text: "Vi använder tekniska och organisatoriska skyddsåtgärder, såsom krypterad överföring och behörighetsstyrning, för att skydda uppgifterna mot obehörig åtkomst, förlust och ändring.",
            },
          ],
        },
        {
          title: "Dina rättigheter",
          paragraphs: [
            {
              text: "Du har rätt till tillgång till dina uppgifter, rättelse och radering, begränsning av och invändning mot behandlingen samt dataportabilitet. Du kan också återkalla ett lämnat samtycke. Du har rätt att lämna klagomål till Integritetsskyddsmyndigheten (IMY).",
            },
          ],
        },
        {
          title: "Lagringstid",
          paragraphs: [
            {
              text: "Vi lagrar personuppgifter endast så länge det behövs för att tillhandahålla tjänsten eller för att uppfylla rättsliga förpliktelser. Därefter raderas eller anonymiseras uppgifterna.",
            },
          ],
        },
        {
          title: "Användning av cookies",
          paragraphs: [
            {
              text: "Tjänsten kan använda cookies och liknande tekniker för att hålla dig inloggad, komma ihåg inställningar och analysera användningen. Du kan hantera cookies i din webbläsares inställningar.",
            },
          ],
        },
        {
          title: "Ändringar av policyn",
          paragraphs: [
            {
              text: "Policyn kan uppdateras. Väsentliga ändringar meddelas i tjänsten eller via e-post.",
            },
          ],
        },
      ],
    },
  },

  legal: {
    companyName: "Licensieringstestet by MIVX",
    companyShortName: "Licensieringstestet",
    governingLaw: "Sverige",
    privacyContactEmail: "go@mivx.com",
    operatorName: "PAUL BREMOND SAS",
    operatorLocation: "Paris, Frankrike",
  },
};

export default siteConfig;
